/**
 * Laravel Echo / Reverb Configuration
 *
 * All settings are read from src/config.json — no .env vars required.
 * Keys used:
 *   reverb_app_key   — Reverb application key
 *   reverb_host      — Reverb host (domain only, no protocol)
 *   reverb_port      — Reverb port (leave empty string to use the default)
 *   reverb_scheme    — "http" or "https"
 *   api_url          — Base URL for the Laravel API (used for broadcasting auth)
 */

import Echo from "laravel-echo";
import Pusher from "pusher-js";
import config from "../config.json";

/**
 * Creates a Laravel Echo instance backed by the Reverb WebSocket server.
 * Returns `null` when called outside a browser context.
 *
 * @param {string} [authToken]  Optional Bearer token. Omit to use cookie auth.
 * @returns {Echo|null}
 */
export const initEcho = (authToken) => {
  if (typeof window === "undefined") return null;

  // Attach Pusher constructor so the Echo adapter can find it globally.
  window.Pusher = Pusher;

  // Build auth headers
  const authHeaders = {
    Accept: "application/json",
  };

  if (authToken) {
    authHeaders["Authorization"] = `Bearer ${authToken}`;
  } else {
    // Cookie-based auth: extract CSRF token when present.
    const csrfToken = document.cookie
      .split("; ")
      .find((row) => row.startsWith("XSRF-TOKEN="))
      ?.split("=")[1];

    if (csrfToken) {
      authHeaders["X-XSRF-TOKEN"] = decodeURIComponent(csrfToken);
    }
  }

  const rawPort = config.reverb_port;
  const wsPort = rawPort ? parseInt(rawPort, 10) : null;

  window.Echo = new Echo({
    broadcaster: "reverb",
    key: config.reverb_app_key,
    wsHost: config.reverb_host,
    wsPort,
    wssPort: wsPort,
    forceTLS: config.reverb_scheme === "https",
    enabledTransports: ["ws", "wss"],
    authEndpoint: `${config.api_url}/api/broadcasting/auth`,
    auth: {
      headers: authHeaders,
    },
    // Send session cookies during the auth handshake when not using a token.
    withCredentials: !authToken,
  });

  return window.Echo;
};
